import React, { type ChangeEvent } from 'react';

import classnames from 'classnames';

import { Icon } from '../../Icon';
import { Input, type InputBaseProps } from '../../Input';

import styles from './DatePickerCalendarCustomInput.module.scss';

const regexDateEUR = /(0[1-9]|[12][0-9]|3[01])(\/|-|.)(0[1-9]|1[0,1,2])(\/|-|.)([12])(\d{3})/;

const regexDateISO = /([12])(\d{3})(\/|-|.)(0[1-9]|1[0,1,2])(\/|-|.)(0[1-9]|[12][0-9]|3[01])/;

const CALENDAR_ICON = 'calendar_icon';

export const parseEurDateToISO = (value: string) => {
    const match = value.match(regexDateEUR);
    if (!match || match?.length === 0) return value;
    const year = match[5] + match[6];
    const month = match[3];
    const day = match[1];

    return `${year}-${month}-${day}`;
};

type DatePickerCalendarCustomInputProps = {
    value: string;
    selectsRange?: boolean;
    showSelectRangeValue?: boolean;
    readOnly?: boolean;
    className?: string;
    onChange?: (event: Omit<ChangeEvent, 'target'> & { target: { value: Date | string } }) => void;
} & InputBaseProps;

const DatePickerCalendarCustomInput = ({
    value: valueProp,
    selectsRange,
    showSelectRangeValue = false,
    readOnly,
    className,
    error,
    onChange,
    ...rest
}: DatePickerCalendarCustomInputProps) => {
    // The value does not need to be displayed
    // in the input field when in range mode.
    const value = selectsRange && !showSelectRangeValue ? '' : valueProp;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event?.target?.value;

        /**
         * Convert to Date value if the input is a valid date format.
         */
        switch (true) {
            case regexDateEUR.test(text):
                onChange?.({
                    ...event,
                    target: {
                        ...event.target,
                        // Due to the default Date standard is ISO 8601, will need convert to ISO format before set value (https://sherpany.slack.com/archives/C04K6S948TV/p1725516922349499)
                        value: new Date(parseEurDateToISO(text))
                    }
                });
                return;
            case regexDateISO.test(text):
                onChange?.({
                    ...event,
                    target: { ...event.target, value: new Date(text) }
                });
                return;
            default:
                onChange?.(event);
        }
    };

    return (
        <Input
            {...rest}
            readOnly={readOnly}
            type="text"
            error={error}
            value={value}
            suffix={
                !readOnly && (
                    <Icon
                        data-testid={CALENDAR_ICON}
                        name="s-icon-calendar-dates"
                        size="medium"
                        color={error ? 'danger' : 'secondary'}
                        onMouseDown={rest.onMouseDown}
                    />
                )
            }
            className={classnames(className, styles.input, {
                [styles.range]: selectsRange,
                [styles.withCustomStyles]: className
            })}
            onChange={handleChange}
        />
    );
};

export { DatePickerCalendarCustomInput };
